import { ApiService, type ApiRequestConfig } from "./api.service";
import type { TIssue, TIssueListResponse, TIssueQuery } from "@/types/issue";

function wrapArchivedResponse(value: TIssue[] | TIssueListResponse): TIssueListResponse {
  if (!Array.isArray(value)) return value;
  return {
    grouped_by: "",
    next_cursor: null,
    prev_cursor: null,
    next_page_results: false,
    prev_page_results: false,
    total_count: value.length,
    count: value.length,
    total_pages: 1,
    extra_stats: null,
    results: value,
    total_results: value.length,
  };
}

export class IssueArchiveService extends ApiService {
  async listArchivedIssues(
    slug: string,
    projectId: string,
    query?: TIssueQuery,
    config?: ApiRequestConfig,
  ): Promise<TIssueListResponse> {
    // The Cognota backend currently returns a bare array (no pagination envelope).
    // Wrap so downstream stores/normalizers see the same shape the active
    // issue list endpoint produces.
    const response = await this.get<TIssue[] | TIssueListResponse>(
      `/api/workspaces/${slug}/projects/${projectId}/archived-work-items/`,
      { ...config, params: query },
    );
    return wrapArchivedResponse(response);
  }

  async getArchivedIssue(slug: string, projectId: string, issueId: string): Promise<TIssue> {
    return this.get(`/api/workspaces/${slug}/projects/${projectId}/work-items/${issueId}/archive/`);
  }

  async archiveIssue(
    slug: string,
    projectId: string,
    issueId: string,
  ): Promise<{ archived_at: string }> {
    return this.post(
      `/api/workspaces/${slug}/projects/${projectId}/work-items/${issueId}/archive/?cascade_children=true`,
    );
  }

  async restoreIssue(slug: string, projectId: string, issueId: string): Promise<void> {
    return this.delete(
      `/api/workspaces/${slug}/projects/${projectId}/work-items/${issueId}/archive/`,
    );
  }

  async bulkArchiveIssues(
    slug: string,
    projectId: string,
    issueIds: string[],
  ): Promise<{ archived_at: string }> {
    return this.post(
      `/api/workspaces/${slug}/projects/${projectId}/bulk-archive-work-items/?cascade_children=true`,
      {
        issue_ids: issueIds,
      },
    );
  }
}
